import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import useSize from "../../../../Utils/useSize/useSize";
import Socket from "../../../../context/SocketContext";
import "./BuySell.css";
import moment from "moment";
import { Label, TextInput } from "flowbite-react";
import toast from "react-hot-toast";
import Standing from "./Standing/Standing";
import SqButton from './SqButton'
import Qty from "./Qty/Qty";
import Bid from "./Bid";
import Ask from "./Ask";
import High from "./High";
import Low from "./Low";
import Ltp from "./Ltp";
import Change from "./Change";
import useSound from "use-sound";
import Loading from "react-loading";
import Backend from "../../../../Backend/Backend";
import ErrorSound from "../../../../Sound/Error.wav";
import BuySound from "../../../../Sound/buy.mp3";
import SellSound from "../../../../Sound/sell.mp3";
import { Dark } from "../../../../Theme/Theme";

const backend = await new Backend().init();

export default function BuySell({
  symbolData,
  buyselltype,
  buysell,
  close,
  pending,
}) {
  const socket = useContext(Socket);
  const windowSize = useSize();
  const Selecter = useRef(null);

  const [errorsound] = useSound(ErrorSound);
  const [buysound] = useSound(BuySound);
  const [sellsound] = useSound(SellSound);

  const [symbolQty, setSymbolQty] = useState("");
  const [usdInr, setUsdInr] = useState(null);

  const [time, setTime] = useState("");
  const [type, setType] = useState(0); // 0=Market,1=Limit,2=Stoploss
  const [loading, setLoading] = useState(false);
  const [qty, setQty] = useState("");
  const [price, setPrice] = useState("");

  /** 🔹 Setup initial values & socket listener */
  useEffect(() => {
    // 🧩 Defensive guard
    if (!symbolData) return null;

    socket.emit("giverate", symbolData.symbol_id);
    socket.on("trade" + symbolData.symbol_id, onTrade);

    Selecter.current?.focus();

    // Default qty setup
    const defaultQty = pending
      ? symbolData.type === "nse" ||
        symbolData.type === "nse_cash" ||
        symbolData.type === "us_stock" ||
        symbolData.type === "sgx"
        ? JSON.parse(symbolData.qty)
        : JSON.parse(symbolData.qty / symbolData.lot_size)
      : symbolData.symbol_display == null
        ? symbolData.type === "nse" ||
          symbolData.type === "nse_cash" ||
          symbolData.type === "us_stock" ||
          symbolData.type === "sgx"
          ? JSON.parse(symbolData.qty)
          : JSON.parse(symbolData.qty / symbolData.lot_size)
        : symbolData.type === "nse" ||
          symbolData.type === "nse_cash" ||
          symbolData.type === "us_stock" ||
          symbolData.type === "sgx"
          ? JSON.parse(symbolData.min_qty)
          : JSON.parse(symbolData.min_qty / symbolData.lot_size);

    setQty(defaultQty || "");

    if (pending) {
      setType(symbolData.limit_sl === 0 ? 1 : 2);
      setPrice(symbolData.price);
    }

    symbols();

    return () => socket.off("trade" + symbolData.symbol_id, onTrade);
  }, [symbolData]);

  /** 🔹 Update tick data time */
  const onTrade = (data) => {
    if (data?.LastUpdateTime && data.LastUpdateTime !== time) {
      setTime(data.LastUpdateTime);
    }
  };

  const symbols = () => {
    setLoading(true);
    let data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
      symbol_id: symbolData.symbol_id,
    };

    backend.symbol_qty(data).then((r) => {
      setLoading(false);
      if (r.error == "False") {
        setSymbolQty(r.symbol);
        setUsdInr(r.usdInr);
      } else {
        toast.error(r.message);
      }
    });
  };

  /** 🔹 Unified order submission */
  const submit = async () => {
    if (!buysell) return;

    // ✅ Validate quantity
    if (!qty || qty <= 0) {
      toast.error("Invalid order quantity!");
      errorsound();
      return;
    }

    // ✅ Validate price only for Limit / StopLoss
    if (type !== 0 && (!price || price <= 0)) {
      toast.error("Invalid order price!");
      errorsound();
      return;
    }

    setLoading(true);

    try {
      // ✅ Build base order object
      const orderData = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        user_id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        qty_order: qty,
        symbol_id: symbolData.symbol_id,
        symbol: symbolData.symbol_display ?? symbolData.symbol,
        symbol_type: symbolData.type,
        type: buyselltype, // Buy or Sell
        buy_sell: type, // 0=Market,1=Limit,2=StopLoss
        ...(type !== 0 && { price }), // ✅ Add price only when needed
        ...(pending && {
          // ✅ Merge pending edit data if applicable
          pending_edit: "1",
          pending_id: symbolData.id,
        }),
      };

      // ✅ Execute backend call
      const r = await backend.buy_sell(orderData);
      setLoading(false);

      if (r.error === "False") {
        toast.success(r.message);
        buyselltype === 0 ? buysound() : sellsound();
        setQty("");
        setPrice("");
        if (pending) close?.();
      } else {
        toast.error(r.message);
        errorsound();
      }
    } catch (err) {
      console.error(err);
      toast.error("Order failed. Try again.");
      errorsound();
      setLoading(false);
    }
  };

  /** 🔹 Cancel handler */
  const cancel = () => !loading && close?.();

  return (
    <div
      className={`${buyselltype === 0 ? "buy" : "sell"} buysellmodal`}
      style={{
        height: windowSize[1] / 3.5,
        width: windowSize[0] / 1.2,
      }}
    >
      <Fragment>
        {/* Header */}
        <div className="buysellType">
          <div className="typeText">{buyselltype === 0 ? "BUY" : "SELL"}</div>
        </div>

        {/* Symbol Info */}
        <div className="buysellSymbol">
          <div
            className={`symbol ${buyselltype === 0 ? "symbolbuy" : "symbolsell"
              }`}
          >
            <div className="symbolName">
              {symbolData.symbol_display ?? symbolData.symbol}
              <div className="symbolexpiry">
                {symbolData.type === "nse_cash"
                  ? "EQUITY"
                  : moment(symbolData.expiry_date).format("DD-MMM")}
              </div>
            </div>
            <Standing key={loading} symbol_id={symbolData.symbol_id} />
          </div>

          {/* Order Section */}
          <div className="buysell">
            <div className="order">
              <div className="enterorder">
                {/* Order Type */}
                <div className="orderselect">
                  <Label
                    value="ORDER TYPE"
                    style={{
                      fontSize: 12,
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  />
                  <select
                    className="selecter"
                    value={type}
                    disabled={pending || loading}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setType(val);
                      if (val === 0) setPrice("");
                    }}
                  >
                    <option value={0}>MARKET</option>
                    <option value={1}>TARGET</option>
                    <option value={2}>STOP-LOSS</option>
                  </select>
                </div>

                {/* Qty / Lot */}
                <div className="orderselect">
                  <Label
                    value={
                      symbolData.type === "nse" ||
                        symbolData.type === "nse_cash" ||
                        symbolData.type === "us_stock" ||
                        symbolData.type === "sgx"
                        ? "QTY"
                        : "LOT"
                    }
                    style={{
                      fontSize: 12,
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  />
                  <TextInput
                    ref={Selecter}
                    shadow
                    type="number"
                    disabled={pending || loading}
                    step="0.01" // ⭐ allows 0.01, 0.1, 0.25 etc.
                    min="0.01" // optional: prevent entering 0 or negative
                    placeholder={
                      symbolData.type === "nse" ||
                        symbolData.type === "nse_cash" ||
                        symbolData.type === "us_stock" ||
                        symbolData.type === "sgx"
                        ? "Enter Qty"
                        : "Enter Lot"
                    }
                    value={qty}
                    onKeyDownCapture={(e) => {
                      if (e.key === "Enter") submit();
                    }}
                    onChange={(e) => setQty(e.target.value)}
                    className="qty mousetrap"
                    required
                  />
                </div>

                {/* Price */}
                <div className="orderselect">
                  <Label
                    value="PRICE"
                    style={{
                      fontSize: 12,
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  />
                  <TextInput
                    shadow
                    type="number"
                    placeholder="Enter Price"
                    disabled={type === 0 || loading}
                    value={price}
                    onKeyDownCapture={(e) => {
                      if (e.key === "Enter") submit();
                    }}
                    step="0.01" // ⭐ allows 0.01, 0.1, 0.25 etc.
                    min="0.01" // optional: prevent entering 0 or negative
                    onChange={(e) => setPrice(e.target.value)}
                    className="qty mousetrap"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="submit">
                <div className="submitContainer">
                  <button
                    onClick={!loading ? submit : undefined}
                    className="btnorder"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loading
                        type="spin"
                        color="#ffffff"
                        height={20}
                        width={20}
                      />
                    ) : (
                      "SUBMIT"
                    )}
                  </button>
                </div>

                <div className="cancelContainer">
                  <button
                    onClick={cancel}
                    className="btncancel"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loading
                        type="spin"
                        color={Dark.text}
                        height={20}
                        width={20}
                      />
                    ) : (
                      "CANCEL"
                    )}
                  </button>
                </div>

                <SqButton
                  key={loading}
                  symbolType={symbolData.type}
                  symbol_id={symbolData.symbol_id}
                />
              </div>
            </div>

            {/* Bid / Ask */}
            <div className="bidhigh">
              <div
                className={`bidcontainer ${buyselltype === 0 ? "symbolbuy" : "symbolsell"
                  }`}
              >
                <Bid symbol_id={symbolData.symbol_id} />
              </div>
              <div
                className={`highcontainer ${buyselltype === 0 ? "symbolbuy" : "symbolsell"
                  }`}
              >
                <High symbol_id={symbolData.symbol_id} />
              </div>

              {/* {usdInr == null ? null : (
                <div
                  className={`highcontainer addTop ${
                    buyselltype === 0 ? "symbolbuy" : "symbolsell"
                  }`}
                >
                  <Bid symbol_id={usdInr.symbol_id} usdInr={1} />
                </div>
              )} */}
            </div>

            <div className="asklow">
              <div
                className={`askcontainer ${buyselltype === 0 ? "symbolbuy" : "symbolsell"
                  }`}
              >
                <Ask symbol_id={symbolData.symbol_id} />
              </div>
              <div
                className={`lowcontainer ${buyselltype === 0 ? "symbolbuy" : "symbolsell"
                  }`}
              >
                <Low symbol_id={symbolData.symbol_id} />
              </div>
              {/* {usdInr == null ? null : (
                <div
                  className={`lowcontainer addTop ${
                    buyselltype === 0 ? "symbolbuy" : "symbolsell"
                  }`}
                >
                  <Ask symbol_id={usdInr.symbol_id} usdInr={1} />
                </div>
              )} */}
            </div>
          </div>
        </div>

        {/* LTP + Change */}
        <div className="buysellData">
          <div className="LtpChnagr">
            <div className="LtpLeft">
              <div className="Ltp">LTP</div>
              <div className="Chnage">CHANGE</div>
            </div>
            <div className="LtpRight">
              <Ltp symbol_id={symbolData.symbol_id} />
              <Change symbol_id={symbolData.symbol_id} />
            </div>
          </div>
          <Qty symbolQty={symbolQty} />
        </div>
      </Fragment>
    </div>
  );
}
